import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMenu } from '@happyspace/sdk/restaurant';
import { UtensilsCrossed, ClipboardList } from 'lucide-react';
import DishCard, { categoryIcon } from './DishCard';
import { sortCategories } from '../lib/site';

const SIGNATURES = [
  'Lamb/Beef Shawarma',
  'Chicken Shawarma Combo',
  'Kabob Sandwich',
  'Shish Kabob Combo',
];

/** Home-page menu teaser: a few category tabs over the first 10 dishes. */
export default function MenuHighlights() {
  const { items, categories, loading } = useMenu();
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const visibleCategories = useMemo(
    () => sortCategories(categories.filter((c) => items.some((i) => i.category === c.name))).slice(0, 6),
    [categories, items],
  );

  const filteredItems = useMemo(() => {
    if (activeCategory !== 'all') {
      return items.filter((i) => i.category === activeCategory).slice(0, 10);
    }
    const featured = SIGNATURES.map((t) => items.find((i) => i.title === t)).filter(
      (i): i is NonNullable<typeof i> => Boolean(i),
    );
    const ids = new Set(featured.map((i) => i._id));
    return [...featured, ...items.filter((i) => !ids.has(i._id))].slice(0, 10);
  }, [items, activeCategory]);

  return (
    <section id="menu" className="relative pt-4 md:pt-6 pb-12 md:pb-16 bg-white">
      <div className="max-w-screen-xl mx-auto px-5 md:px-8">
        <div className="text-center">
          <span
            className="inline-flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.25em]"
            style={{ color: 'var(--color-ink)' }}
          >
            <span className="h-px w-8" style={{ background: 'var(--color-yellow)' }} />
            Our Menu
            <span className="h-px w-8" style={{ background: 'var(--color-yellow)' }} />
          </span>
          <h2 className="mt-3 display text-4xl md:text-5xl text-ink">Popular Items</h2>
        </div>

        {visibleCategories.length > 0 && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {[{ _id: 'all', name: 'All' }, ...visibleCategories].map((cat) => {
              const key = cat._id === 'all' ? 'all' : cat.name;
              const Icon = cat._id === 'all' ? UtensilsCrossed : categoryIcon(cat.name);
              const isActive = activeCategory === key;
              return (
                <button
                  key={cat._id}
                  onClick={() => setActiveCategory(key)}
                  className="inline-flex items-center gap-2 rounded-md border-2 px-5 py-2.5 text-xs font-extrabold uppercase tracking-wide transition-all duration-200"
                  style={
                    isActive
                      ? {
                          background: 'var(--color-ink)',
                          borderColor: 'var(--color-ink)',
                          color: '#fff',
                        }
                      : { background: '#fff', borderColor: 'var(--color-line)', color: 'var(--color-ink)' }
                  }
                >
                  <Icon size={15} style={{ color: isActive ? 'var(--color-yellow)' : 'var(--color-ink)'}} />
                  {cat.name}
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {loading &&
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-[380px] rounded-lg border animate-pulse"
                style={{ borderColor: 'var(--color-line)', background: 'var(--color-cream)' }}
              />
            ))}
          {!loading && filteredItems.length === 0 && (
            <p className="col-span-full text-center text-ink-2 py-10">
              Menu coming soon &mdash; check back shortly.
            </p>
          )}
          {!loading && filteredItems.map((item, i) => <DishCard key={item._id} item={item} index={i} />)}
        </div>

        <div className="mt-10 text-center">
          <Link to="/menu" className="btn-outline">
            <ClipboardList size={16} /> View Full Menu
          </Link>
        </div>
      </div>
    </section>
  );
}
